import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingCartIcon, 
  XMarkIcon, 
  ChevronUpIcon,
  ChevronDownIcon,
  PlusIcon,
  MinusIcon
} from '@heroicons/react/24/outline';

const AgentStickyCart = ({ 
  selectedProducts = [], 
  subtotal = 0, 
  tax = 0, 
  total = 0,
  onRemoveProduct,
  onUpdateQuantity,
  onSubmitOrder,
  submitting = false,
  companyName = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const itemCount = selectedProducts.length;

  // Always show the cart bar, but only show expanded drawer when there are items
  return (
    <>
      {/* Expanded Cart Drawer - Show on mobile and tablet */}
      {isExpanded && itemCount > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60]">
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-600 to-blue-600 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShoppingCartIcon className="h-6 w-6" />
                  <div>
                    <h3 className="font-bold text-lg">סיכום הזמנה</h3>
                    {companyName && (
                      <p className="text-sm opacity-90">{companyName}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Cart Items */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {selectedProducts.map((product) => (
                  <div key={product.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-start gap-3">
                      {/* Product Image */}
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex-shrink-0 overflow-hidden">
                        {product.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <ShoppingCartIcon className="h-6 w-6" />
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                          {product.name}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          ₪{product.unit_price?.toFixed(2)} × {product.quantity}
                        </p>
                        
                        {/* Bundle indicator */}
                        {product.is_bundle_item && (
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              חלק מחבילה
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onUpdateQuantity(product.id, product.quantity - 1, product)}
                          disabled={product.quantity <= 1}
                          className="p-1 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                        >
                          <MinusIcon className="h-4 w-4" />
                        </button>
                        <span className="w-8 text-center font-medium text-sm">
                          {product.quantity}
                        </span>
                        <button
                          onClick={() => onUpdateQuantity(product.id, product.quantity + 1, product)}
                          className="p-1 rounded-full bg-gray-200 hover:bg-gray-300 touch-manipulation"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => onRemoveProduct(product.id, product.is_tester || false, product.is_bonus || false)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded-full transition-colors touch-manipulation"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-4 border-t">
              <div className="space-y-3">
                {/* Totals */}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">סה"כ מוצרים:</span>
                    <span className="font-medium">₪{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">מע"מ:</span>
                    <span className="font-medium">₪{tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>סה"כ לתשלום:</span>
                    <span className="text-primary-600">₪{total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  onClick={onSubmitOrder}
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-primary-600 to-blue-600 text-white py-3 rounded-lg font-bold text-lg hover:from-primary-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation shadow-lg"
                >
                  {submitting ? 'יוצר הזמנה...' : 'צור הזמנה'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Cart Bar - Clean and beautiful design */}
      <div className="fixed bottom-0 left-0 right-0 z-[60] bg-white border-t border-gray-200 shadow-lg">
        <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Cart Info */}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-3 flex-1"
              >
                <div className="relative">
                  <div className="bg-primary-100 p-2 rounded-full">
                    <ShoppingCartIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {itemCount}
                  </span>
                </div>
                
                <div className="text-right flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {itemCount > 0 ? `${itemCount} ${itemCount === 1 ? 'מוצר' : 'מוצרים'}` : 'העגלה ריקה'}
                  </div>
                  <div className="text-lg font-bold text-primary-600">
                    ₪{total.toFixed(2)}
                  </div>
                </div>
              </button>

              {/* Expand/Collapse Button */}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                ) : (
                  <ChevronUpIcon className="h-5 w-5 text-gray-600" />
                )}
              </button>

              {/* Quick Submit Button */}
              <button
                onClick={onSubmitOrder}
                disabled={submitting || itemCount === 0}
                className="bg-gradient-to-r from-primary-600 to-blue-600 text-white px-4 py-2 rounded-lg font-bold hover:from-primary-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation shadow-sm"
              >
                {submitting ? '...' : itemCount === 0 ? 'ריק' : 'צור'}
              </button>
            </div>
          </div>
        </div>
    </>
  );
};

export default AgentStickyCart;
